import { useEffect, useMemo, useState } from "react";
import SEO from "../components/common/SEO";
import PageShell from "../components/layout/PageShell/PageShell";
import SolutionsHero from "../components/solutions/SolutionsHero";
import SolutionsFilterBar from "../components/solutions/SolutionsFilterBar";
import SolutionsGrid from "../components/solutions/SolutionsGrid";
import SolutionsFinalCta from "../components/solutions/SolutionsFinalCta";
import { solutionsListing } from "../content/solutionsContent";
import { api, mediaUrl } from "../api/client";
import styles from "./Solutions.module.css";

function categoryLabel(category) {
  const value = String(category || "").trim();
  if (!value) return "Solution";
  return value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function mapSolution(item) {
  return {
    ...item,
    badge: categoryLabel(item.category),
    image: mediaUrl(item.hero?.mockup?.url || item.image),
    description: item.shortDescription || "",
    tags: Array.isArray(item.tags) ? item.tags : item.categories || [],
  };
}

function categoriesFromSolutions(solutions) {
  const seen = new Map();
  solutions.forEach((item) => {
    (item.tags || []).forEach((tag) => {
      const label = String(tag || "").trim();
      if (!label) return;
      const key = label.toLowerCase();
      if (!seen.has(key)) seen.set(key, label);
    });
  });
  return ["All", ...Array.from(seen.values()).sort((a, b) => a.localeCompare(b))];
}

export default function Solutions() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [solutions, setSolutions] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .getSolutions()
      .then((rows) => {
        if (!alive) return;
        setSolutions(rows.map(mapSolution));
        setError("");
      })
      .catch((err) => {
        if (!alive) return;
        setSolutions([]);
        setError(err.message || "Failed to load solutions");
      });
    return () => {
      alive = false;
    };
  }, []);

  const filters = useMemo(() => categoriesFromSolutions(solutions), [solutions]);

  useEffect(() => {
    if (!filters.some((f) => f.toLowerCase() === activeFilter.toLowerCase())) {
      setActiveFilter("All");
    }
  }, [filters, activeFilter]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return solutions.filter((item) => {
      const matchesFilter =
        activeFilter === "All" ||
        (item.tags || []).some(
          (tag) => tag.toLowerCase() === activeFilter.toLowerCase()
        );
      if (!matchesFilter) return false;
      if (!query) return true;
      const haystack = [item.name, item.description, item.badge, ...(item.tags || [])]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [activeFilter, search, solutions]);

  const { meta, hero, searchPlaceholder, cta } = solutionsListing;

  return (
    <PageShell atmosphere="products">
      <SEO title={meta.title} description={meta.description} path="/solutions" />
      <div className={styles.page}>
        <SolutionsHero content={hero} />
        <div className={styles.filterWrap}>
          <SolutionsFilterBar
            filters={filters}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder={searchPlaceholder}
          />
        </div>
        {error ? <p style={{ textAlign: "center", padding: 24 }}>{error}</p> : null}
        <SolutionsGrid items={filtered} />
        <SolutionsFinalCta content={cta} />
      </div>
    </PageShell>
  );
}
